import { jobRegistry, type JobTypes } from "../Services/JobRegistry";
import { parseJobReference } from "../Services/JobSignalService";

function appendSocialPromptDeeplinkParams(
  params: URLSearchParams,
  shouldOpenSocialPrompt: boolean,
  socialPromptSeriesId?: string | null
) {
  if (!shouldOpenSocialPrompt) return;
  params.set("openSocialPrompt", "1");
  if (
    typeof socialPromptSeriesId === "string" &&
    socialPromptSeriesId.trim().length > 0
  ) {
    params.set("socialPromptSeriesId", socialPromptSeriesId.trim());
  }
}

/** Account page → Insights tab → insights table (not signals or contacts workspace). */
function appendAccountInsightsTableParams(params: URLSearchParams) {
  params.set("tab", "insights");
  params.delete("workspace");
  params.set("insightsView", "table");
}

/** Canonical library destination; native IDs are resolved by the authorized API. */
export function buildInsightsLibraryDeeplink(options: {
  clientId?: string | null;
  itemId?: string | null;
  industryInsightId?: number | null;
  openSocial?: boolean;
  seriesId?: string | null;
} = {}): string {
  const params = new URLSearchParams();
  if (options.clientId) params.set('clientId', options.clientId);
  if (options.itemId) params.set('insight', options.itemId);
  else if (options.industryInsightId != null) {
    params.set('origin', 'industry_insight');
    params.set('originId', String(options.industryInsightId));
  }
  if (options.openSocial) {
    if (options.seriesId?.trim()) params.set('socialPromptId', options.seriesId.trim());
    else params.set('social', '1');
  }
  return `/insights/all${params.size ? `?${params}` : ''}`;
}

export const handleAlertNavigation = (
  linkAction: {
    link_type: string;
    job?: unknown;
    client_id?: string | null;
    account_id: string | null;
    contact_id: number | null;
    insight_id: number | null;
    insight_item_id?: string | null;
    asset_id: number | null;
    asset_source_id?: string | null;
    date?: string | null;
    action_id?: string | null;
    user_id?: string | null;
    trend_id?: string | null;
    week_start?: string | null;
    social_prompt_series_id?: string | null;
    social_prompt_suggestion_id?: string | null;
    decision_id?: string | null;
  },
  options?: {
    alertType?:
      | "CONTACT_READY"
      | "ACTION_READY"
      | "SYSTEM"
      | "DISMISSAL_AUTOMATION"
      | "SOCIAL_PROMPT_PREFERENCE_SUGGESTION";
  }
) => {
  const {
    link_type,
    client_id,
    account_id,
    contact_id,
    insight_id,
    insight_item_id,
    asset_id,
    date,
    action_id,
    user_id,
    trend_id,
    week_start,
    social_prompt_series_id,
    social_prompt_suggestion_id,
  } = linkAction;
  const { alertType } = options || {};
  const shouldOpenSocialPrompt = alertType === 'ACTION_READY';

  switch (link_type) {
    case 'JOB': {
      const job = parseJobReference(linkAction.job);
      const registration = job && Object.hasOwn(jobRegistry, job.kind)
        ? jobRegistry[job.kind as keyof JobTypes] : undefined;
      if (job && registration?.owner === job.owner && client_id) {
        window.location.href = registration.destination(job.id, { clientId: client_id, actorId: '' });
      }
      break;
    }
    case 'ASSET_SOURCES': {
      const params = new URLSearchParams({ view: 'sources' });
      if (client_id) params.set('clientId', client_id);
      if (linkAction.asset_source_id) params.set('sourceId', linkAction.asset_source_id);
      window.location.href = `/my-assets?${params.toString()}`;
      break;
    }
    case 'DISMISSAL_DECISION':
      if (linkAction.decision_id) window.location.href = `/dismissal-decisions/${encodeURIComponent(linkAction.decision_id)}`;
      break;
    case 'CONTACT':
      if (account_id && contact_id) {
        const params = new URLSearchParams();
        appendAccountInsightsTableParams(params);
        params.set('highlightContact', contact_id.toString());
        appendSocialPromptDeeplinkParams(
          params,
          shouldOpenSocialPrompt,
          social_prompt_series_id
        );
        window.location.href = `/account/${account_id}?${params.toString()}`;
      } else if (account_id) {
        window.location.href = `/account/${account_id}`;
      }
      break;

    case 'ACCOUNT':
      if (account_id) {
        window.location.href = `/account/${account_id}`;
      }
      break;

    case 'ACCOUNT_INSIGHT':
      if (account_id && insight_id) {
        const params = new URLSearchParams();
        appendAccountInsightsTableParams(params);
        params.set('highlightAccountInsight', insight_id.toString());
        appendSocialPromptDeeplinkParams(
          params,
          shouldOpenSocialPrompt,
          social_prompt_series_id
        );
        window.location.href = `/account/${account_id}?${params.toString()}`;
      } else if (account_id) {
        window.location.href = `/account/${account_id}`;
      }
      break;

    case 'INSIGHTS_ITEM':
      if (client_id && insight_item_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(insight_item_id)) {
        window.location.href = buildInsightsLibraryDeeplink({ clientId: client_id, itemId: insight_item_id, openSocial: shouldOpenSocialPrompt, seriesId: social_prompt_series_id });
      }
      break;

    case 'INDUSTRY_INSIGHT':
      if (account_id && insight_id) {
        const params = new URLSearchParams();
        appendAccountInsightsTableParams(params);
        params.set('highlightIndustryInsight', insight_id.toString());
        appendSocialPromptDeeplinkParams(
          params,
          shouldOpenSocialPrompt,
          social_prompt_series_id
        );
        window.location.href = `/account/${account_id}?${params.toString()}`;
      } else if (insight_id) {
        window.location.href = buildInsightsLibraryDeeplink({ clientId: client_id, industryInsightId: insight_id, openSocial: shouldOpenSocialPrompt, seriesId: social_prompt_series_id });
      } else {
        window.location.href = buildInsightsLibraryDeeplink({ clientId: client_id });
      }
      break;

    case 'ASSET_INSIGHT':
      if (asset_id) {
        const params = new URLSearchParams();
        if (client_id) {
          params.set('clientId', client_id);
        }
        params.set('highlightAsset', asset_id.toString());
        appendSocialPromptDeeplinkParams(
          params,
          shouldOpenSocialPrompt,
          social_prompt_series_id
        );
        window.location.href = `/my-assets?${params.toString()}`;
      } else {
        window.location.href = '/my-assets';
      }
      break;

    case 'CALENDAR_ACTION':
      if (account_id) {
        const params = new URLSearchParams();
        params.set('tab', 'calendar');
        params.set('view', 'day');
        if (date) params.set('date', date);
        if (action_id) params.set('actionId', action_id);
        window.location.href = `/account/${account_id}?${params.toString()}`;
      }
      break;

    case 'THOUGHT_LEADERSHIP':
      if (user_id && trend_id) {
        window.location.href = buildThoughtLeadershipDeeplink({
          user_id,
          trend_id,
          client_id,
          week_start,
        });
      }
      break;

    case 'SOCIAL_PROMPT_PREFERENCES':
      if (social_prompt_suggestion_id) {
        const params = new URLSearchParams();
        params.set('socialPromptSuggestion', social_prompt_suggestion_id);
        window.location.href = `/settings?${params.toString()}#settings-outreach`;
      } else {
        window.location.href = '/settings#settings-outreach';
      }
      break;

    default:
      break;
  }
};

/**
 * Builds an internal app deeplink URL for a candidate action's source.
 * Returns null when there is insufficient data to construct a meaningful URL.
 *
 * Handles:
 *  - ACCOUNT_INSIGHT  → /account/:id?tab=insights&highlightAccountInsight=:insight_id
 *  - INDUSTRY_INSIGHT → account insight table, or the canonical library origin resolver
 *  - ASSET_INSIGHT    → /my-assets?highlightAsset=:insight_id
 *  - Contact source   → /account/:id?tab=insights&highlightContact=:contact_id
 */
export const buildCandidateActionDeeplink = (params: {
  source_insight_type: string | null;
  source_insight_id: number | null;
  source_contact_id: number | null;
  account_id: string | null;
  client_id?: string | null;
}): string | null => {
  const { source_insight_type, source_insight_id, source_contact_id, account_id, client_id } = params;
  const insightType = source_insight_type?.toUpperCase() ?? "";

  if (source_insight_id != null) {
    if (insightType.includes("ASSET")) {
      const p = new URLSearchParams();
      if (client_id) p.set("clientId", client_id);
      p.set("highlightAsset", source_insight_id.toString());
      return `/my-assets?${p.toString()}`;
    }

    if (insightType.includes("INDUSTRY")) {
      if (account_id) {
        const p = new URLSearchParams();
        appendAccountInsightsTableParams(p);
        p.set("highlightIndustryInsight", source_insight_id.toString());
        return `/account/${account_id}?${p.toString()}`;
      }
      return buildInsightsLibraryDeeplink({ clientId: client_id, industryInsightId: source_insight_id });
    }

    // Default: ACCOUNT_INSIGHT
    if (account_id) {
      const p = new URLSearchParams();
      appendAccountInsightsTableParams(p);
      p.set("highlightAccountInsight", source_insight_id.toString());
      return `/account/${account_id}?${p.toString()}`;
    }
  }

  if (source_contact_id != null && account_id) {
    const p = new URLSearchParams();
    appendAccountInsightsTableParams(p);
    p.set("highlightContact", source_contact_id.toString());
    return `/account/${account_id}?${p.toString()}`;
  }

  return null;
};

/**
 * Account page → Insights tab → Contacts workspace, with the contact row highlighted/scrolled into view.
 * Aligns with AccountDetailsView handling of highlightContact + workspace=contacts.
 */
export const buildAccountContactHighlightDeeplink = (
  accountId: string,
  contactId: number
): string => {
  const p = new URLSearchParams();
  p.set("tab", "insights");
  p.set("workspace", "contacts");
  p.set("highlightContact", String(contactId));
  return `/account/${accountId}?${p.toString()}`;
};

/**
 * Builds a deep link URL directly to the account calendar day view,
 * optionally auto-opening a specific action modal by its action_id.
 *
 * Example output:
 *   /account/123?tab=calendar&view=day&date=2026-03-18&actionId=abc123
 */
export const buildCalendarDeeplink = (params: {
  account_id: string;
  date: string;
  action_id?: string;
}): string => {
  const p = new URLSearchParams();
  p.set("tab", "calendar");
  p.set("view", "day");
  p.set("date", params.date);
  if (params.action_id) {
    p.set("actionId", params.action_id);
  }
  return `/account/${params.account_id}?${p.toString()}`;
};

/**
 * Builds a deep link URL to the Sales Home Thought Leadership section for a
 * specific rep, auto-opening the modal for the target trend.
 *
 * Consumed by DailyActionsView, which:
 *   - (admin) sets selectedClientId from tlClientId
 *   - (leader-like) sets repFilterUserIds to [tlUserId] so the TL plan loads
 *   - finds the entry with matching trend_id and opens ThoughtLeadershipModal
 *   - falls back to a deep-link-miss banner when the trend is no longer present
 *
 * Example output:
 *   /sales-home?tlUserId=abc&tlTrendId=trend-123&tlClientId=42&tlWeekStart=2026-04-20
 */
export const buildThoughtLeadershipDeeplink = (params: {
  user_id: string;
  trend_id: string;
  client_id?: string | null;
  week_start?: string | null;
}): string => {
  const p = new URLSearchParams();
  p.set("tlUserId", params.user_id);
  p.set("tlTrendId", params.trend_id);
  if (params.client_id) p.set("tlClientId", params.client_id);
  if (params.week_start) p.set("tlWeekStart", params.week_start);
  return `/sales-home?${p.toString()}`;
};
